import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { ChatSession } from "../../../types";
import { join } from "../../../utils/classNames";
import { Kebab } from "../../icons";
import { SessionMenu } from "../../SessionMenu/SessionMenu";
import styles from "./SessionItem.module.css";

interface Props {
	session: ChatSession;
	active: boolean;
	onSwitch: (id: string) => void;
	onDelete: (id: string) => void;
	onRename: (id: string, name: string) => void;
	onToggleFavourite: (id: string) => void;
}

export const SessionItem: React.FC<Props> = ({ session, active, onSwitch, onDelete, onRename, onToggleFavourite }) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [renaming, setRenaming] = useState(false);
	const [draft, setDraft] = useState(session.name);
	const inputRef = useRef<HTMLInputElement>(null);

	const startRename = () => {
		setDraft(session.name);
		setRenaming(true);
	};

	useEffect(() => {
		if (renaming && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [renaming]);

	const commitRename = () => {
		if (!renaming) return;
		setRenaming(false);
		const trimmed = draft.trim();
		if (trimmed && trimmed !== session.name) onRename(session.id, trimmed);
	};

	if (renaming) {
		return (
			<div className={join(styles.item, active && styles.itemActive)}>
				<input
					ref={inputRef}
					className={styles.renameInput}
					value={draft}
					aria-label="Rename conversation"
					onChange={(e) => setDraft(e.target.value)}
					onBlur={commitRename}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							commitRename();
						} else if (e.key === "Escape") {
							e.preventDefault();
							setRenaming(false);
						}
					}}
				/>
			</div>
		);
	}

	return (
		<div
			role="button"
			tabIndex={0}
			className={join(styles.item, active && styles.itemActive, menuOpen && styles.menuOpen)}
			onClick={() => onSwitch(session.id)}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onSwitch(session.id);
				}
			}}
		>
			<span className={styles.itemLabel}>{session.name || "New Chat"}</span>
			<SessionMenu
				favourite={session.favourite}
				onToggleFavourite={() => onToggleFavourite(session.id)}
				onRename={startRename}
				onDelete={() => onDelete(session.id)}
				onOpenChange={setMenuOpen}
				triggerClassName={styles.kebab}
				triggerAriaLabel="Conversation options"
				triggerContent={<Kebab />}
				align="right"
				side="auto"
			/>
		</div>
	);
};
